import { useFormikContext } from "formik";
import _get from "lodash/get";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Grid } from "semantic-ui-react";

import { ShareDraftButton } from "@js/invenio_app_rdm/deposit/ShareDraftButton";
import {
  DepositStatusBox,
  PreviewButton,
  SaveButton,
} from "@js/invenio_rdm_records";
import { SubmitReviewModal } from "@js/invenio_rdm_records/src/deposit/controls/PublishButton/SubmitReviewModal";
import { submitReview } from "@js/invenio_rdm_records/src/deposit/state/actions";
import { DepositStatus } from "@js/invenio_rdm_records/src/deposit/state/reducers/deposit";
import { DRAFT_SUBMIT_REVIEW_FAILED_WITH_VALIDATION_ERRORS } from "@js/invenio_rdm_records/src/deposit/state/types";
import { scrollTop } from "@js/invenio_rdm_records/src/deposit/utils";
import { i18next } from "@translations/invenio_rdm_records/i18next";

export const GuidedDepositStatusCard = ({
  record,
  permissions,
  groupsEnabled,
}) => {
  const dispatch = useDispatch();
  const { values, isSubmitting, setErrors, setSubmitting } = useFormikContext();
  const [modalOpen, setModalOpen] = useState(false);

  const filesState = useSelector((state) => state.files);
  const editorState = useSelector((state) => state.deposit.editorState);
  const actionStateExtra = useSelector(
    (state) => state.deposit.actionStateExtra
  );
  const publishModalExtraContent = useSelector(
    (state) => state.deposit.config.publish_modal_extra
  );
  const isDOIRequired = useSelector(
    (state) => state.deposit.config.is_doi_required
  );
  const noINeedDOI = useSelector((state) => state.deposit.noINeedDOI);
  const liveRecord = useSelector((state) => state.deposit.record);

  const community = editorState?.selectedCommunity;
  const disableSubmitForReviewButton =
    editorState?.ui?.disableSubmitForReviewButton;
  const isRecordSubmittedForReview =
    liveRecord?.status === DepositStatus.IN_REVIEW;

  const doiReservationCheck = () => {
    const shouldCheckForExplicitDOIReservation =
      isDOIRequired !== undefined &&
      !isDOIRequired &&
      noINeedDOI &&
      Object.keys(values?.pids?.doi || {}).length === 0;

    if (shouldCheckForExplicitDOIReservation) {
      const errors = {
        pids: {
          doi: i18next.t(
            "DOI is needed. You need to reserve a DOI before submitting for review."
          ),
        },
      };
      setErrors(errors);
      dispatch({
        type: DRAFT_SUBMIT_REVIEW_FAILED_WITH_VALIDATION_ERRORS,
        payload: { data: values, errors },
      });
    }
    return shouldCheckForExplicitDOIReservation;
  };

  const canPerformDraftActions = () => {
    // In some deployments the UI initial permissions are computed with `record=None`
    // and then never refreshed after the draft is created, causing `can_manage=false`
    // even for the creator. Be permissive here by accepting alternative signals.
    return Boolean(
      permissions?.can_manage ||
        permissions?.can_update ||
        permissions?.can_manage_files ||
        permissions?.can_manage_record_access
    );
  };

  const isDisabled = () => {
    if (!community) return true;
    const isNewUpload = !liveRecord?.id;

    if (!isNewUpload && !canPerformDraftActions()) {
      return true;
    }
    if (disableSubmitForReviewButton || isSubmitting) {
      return true;
    }

    const filesEnabled = _get(values, "files.enabled", false);
    const filesArray = Object.values(filesState?.entries ?? {});
    const filesMissing = filesEnabled && filesArray.length === 0;
    if (filesMissing) {
      return true;
    }

    const allCompleted = filesArray.every((file) => file.status === "finished");
    return !allCompleted;
  };

  const handleSubmitReview = ({ reviewComment }) => {
    if (doiReservationCheck()) {
      return;
    }

    setModalOpen(false);
    setSubmitting(true);
    dispatch(submitReview(values, { reviewComment, directPublish: false }))
      .catch((error) => {
        if (error && error.errors) {
          setErrors(error.errors);
        } else {
          scrollTop();
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const btnLblSubmitReview = isRecordSubmittedForReview
    ? i18next.t("Submitted for review")
    : i18next.t("Submit for review");

  return (
    <Card>
      <Card.Content>
        <DepositStatusBox />
      </Card.Content>
      <Card.Content>
        <Grid relaxed>
          <Grid.Column computer={8} mobile={16} className="pb-0 left-btn-col">
            <SaveButton fluid />
          </Grid.Column>
          <Grid.Column computer={8} mobile={16} className="pb-0 right-btn-col">
            <PreviewButton fluid />
          </Grid.Column>
          <Grid.Column width={16} className="pt-10">
            <Button
              disabled={isDisabled()}
              name="SubmitReview"
              onClick={() => setModalOpen(true)}
              primary
              icon="upload"
              labelPosition="left"
              content={btnLblSubmitReview}
              fluid
              type="button"
            />
          </Grid.Column>
          <Grid.Column width={16} className="pt-0">
            {(record?.is_draft === null || canPerformDraftActions()) && (
              <ShareDraftButton
                record={record}
                permissions={permissions}
                groupsEnabled={groupsEnabled}
              />
            )}
          </Grid.Column>
        </Grid>
      </Card.Content>
      {modalOpen && community && (
        <SubmitReviewModal
          isConfirmModalOpen={modalOpen}
          initialReviewComment={actionStateExtra.reviewComment}
          onSubmit={handleSubmitReview}
          community={community}
          onClose={() => setModalOpen(false)}
          publishModalExtraContent={publishModalExtraContent}
          directPublish={false}
          record={null}
        />
      )}
    </Card>
  );
};
